"use strict";

/**
  * @reach/descendants v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */

// src/reach-descendants.tsx
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.array.push.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.define-properties.js";
import "core-js/modules/es.object.define-property.js";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["element", "index"];
import "core-js/modules/es.array.find-index.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.object.values.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.sort.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from "react";
import { useForceUpdate, useIsomorphicLayoutEffect as useLayoutEffect, noop } from "@reach/utils";
function createDescendantContext(name) {
  var initialValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var descendants = [];
  var ctx = /*#__PURE__*/React.createContext(_objectSpread({
    descendants: descendants,
    registerDescendant: function registerDescendant() {
      return noop;
    }
  }, initialValue));
  ctx.displayName = name;
  return ctx;
}
function useDescendant(descendant, context, indexProp) {
  var forceUpdate = useForceUpdate();
  var _React$useContext = React.useContext(context),
    registerDescendant = _React$useContext.registerDescendant,
    descendants = _React$useContext.descendants;
  var index = indexProp !== null && indexProp !== void 0 ? indexProp : descendants.findIndex(function (item) {
    return item.element === descendant.element;
  });
  useLayoutEffect(function () {
    if (!descendant.element) forceUpdate();
    return registerDescendant(_objectSpread(_objectSpread({}, descendant), {}, {
      index: index
    }));
  }, [descendant, forceUpdate, index, registerDescendant].concat(_toConsumableArray(Object.values(descendant))));
  return index;
}
function useDescendantsInit() {
  return React.useState([]);
}
function useDescendants(ctx) {
  return React.useContext(ctx).descendants;
}
function DescendantProvider(_ref) {
  var Ctx = _ref.context,
    children = _ref.children,
    items = _ref.items,
    set = _ref.set;
  var registerDescendant = React.useCallback(function (_ref2) {
    var element = _ref2.element,
      explicitIndex = _ref2.index,
      rest = _objectWithoutProperties(_ref2, _excluded);
    if (!element) return noop;
    set(function (items2) {
      if (explicitIndex != null && explicitIndex !== -1) {
        return insertAt(items2, _objectSpread({
          element: element,
          index: explicitIndex
        }, rest), explicitIndex);
      }
      if (items2.length === 0) {
        return [_objectSpread(_objectSpread({}, rest), {}, {
          element: element,
          index: 0
        })];
      }
      if (true) {
        if (items2.find(function (item) {
          return item.element === element;
        })) {
          console.warn("[reach-ui]: `useDescendant` was called with an element that was already registered.\n\nIf you are using the `@reach/descendants` package directly, make sure to only register descendants once. Registering a descendant more than once will lead to bugs in your app.\n\nIf you are using another `reach-ui` package, this is probably our bug!\n\nTo report a bug, open a new issue:\n\n  https://github.com/reach/reach-ui/issues/new?assignees=&labels=&template=Bug_report.md\n\nIMPORTANT: this check will only run in the development build, so if you see this warning please be aware that your app may crash when deployed.");
          return updateIndices(items2);
        }
      }
      var index = findDOMIndex(items2, element);
      var newItems;
      if (index === -1) {
        newItems = [].concat(_toConsumableArray(items2), [_objectSpread(_objectSpread({}, rest), {}, {
          element: element,
          index: items2.length
        })]);
      } else {
        newItems = insertAt(items2, _objectSpread(_objectSpread({}, rest), {}, {
          element: element,
          index: index
        }), index);
      }
      return newItems;
    });
    return function () {
      if (!element) return;
      set(function (items2) {
        return items2.filter(function (item) {
          return element !== item.element;
        });
      });
    };
  }, []);
  return /* @__PURE__ */React.createElement(Ctx.Provider, {
    value: React.useMemo(function () {
      return {
        descendants: items,
        registerDescendant: registerDescendant
      };
    }, [items, registerDescendant])
  }, children);
}
function useDescendantKeyDown(context, options) {
  var _React$useContext2 = React.useContext(context),
    descendants = _React$useContext2.descendants;
  var callback = options.callback,
    currentIndex = options.currentIndex,
    filter = options.filter,
    _options$key = options.key,
    key = _options$key === void 0 ? "index" : _options$key,
    _options$orientation = options.orientation,
    orientation = _options$orientation === void 0 ? "vertical" : _options$orientation,
    _options$rotate = options.rotate,
    rotate = _options$rotate === void 0 ? true : _options$rotate,
    _options$rtl = options.rtl,
    rtl = _options$rtl === void 0 ? false : _options$rtl;
  return function handleKeyDown(event) {
    if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "PageUp", "PageDown", "Home", "End"].includes(event.key)) {
      return;
    }
    var index = currentIndex !== null && currentIndex !== void 0 ? currentIndex : -1;
    var selectableDescendants = filter ? descendants.filter(filter) : descendants;
    if (!selectableDescendants.length) {
      return;
    }
    var selectableIndex = selectableDescendants.findIndex(function (descendant) {
      return descendant.index === currentIndex;
    });
    function getNextOption() {
      var atBottom = index === getLastOption().index;
      return atBottom ? rotate ? getFirstOption() : selectableDescendants[selectableIndex] : selectableDescendants[(selectableIndex + 1) % selectableDescendants.length];
    }
    function getPreviousOption() {
      var atTop = index === getFirstOption().index;
      return atTop ? rotate ? getLastOption() : selectableDescendants[selectableIndex] : selectableDescendants[(selectableIndex - 1 + selectableDescendants.length) % selectableDescendants.length];
    }
    function getFirstOption() {
      return selectableDescendants[0];
    }
    function getLastOption() {
      return selectableDescendants[selectableDescendants.length - 1];
    }
    switch (event.key) {
      case "ArrowDown":
        if (orientation === "vertical" || orientation === "both") {
          event.preventDefault();
          var next = getNextOption();
          callback(key === "option" ? next : next[key]);
        }
        break;
      case "ArrowUp":
        if (orientation === "vertical" || orientation === "both") {
          event.preventDefault();
          var prev = getPreviousOption();
          callback(key === "option" ? prev : prev[key]);
        }
        break;
      case "ArrowLeft":
        if (orientation === "horizontal" || orientation === "both") {
          event.preventDefault();
          var nextOrPrev = (rtl ? getNextOption : getPreviousOption)();
          callback(key === "option" ? nextOrPrev : nextOrPrev[key]);
        }
        break;
      case "ArrowRight":
        if (orientation === "horizontal" || orientation === "both") {
          event.preventDefault();
          var prevOrNext = (rtl ? getPreviousOption : getNextOption)();
          callback(key === "option" ? prevOrNext : prevOrNext[key]);
        }
        break;
      case "PageUp":
        event.preventDefault();
        var prevOrFirst = (event.ctrlKey ? getPreviousOption : getFirstOption)();
        callback(key === "option" ? prevOrFirst : prevOrFirst[key]);
        break;
      case "Home":
        event.preventDefault();
        var first = getFirstOption();
        callback(key === "option" ? first : first[key]);
        break;
      case "PageDown":
        event.preventDefault();
        var nextOrLast = (event.ctrlKey ? getNextOption : getLastOption)();
        callback(key === "option" ? nextOrLast : nextOrLast[key]);
        break;
      case "End":
        event.preventDefault();
        var last = getLastOption();
        callback(key === "option" ? last : last[key]);
        break;
    }
  };
}
function isElementPreceding(a, b) {
  return Boolean(b.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_PRECEDING);
}
function findDOMIndex(items, element) {
  if (!element) return -1;
  if (!items.length) return -1;
  var length = items.length;
  while (length--) {
    var currentElement = items[length].element;
    if (!currentElement) continue;
    if (isElementPreceding(currentElement, element)) {
      return length + 1;
    }
  }
  return -1;
}
function insertAt(array, item, index) {
  if (index == null || !(index in array)) {
    return [].concat(_toConsumableArray(array), [item]);
  }
  return [].concat(_toConsumableArray(array.slice(0, index)), [item], _toConsumableArray(array.slice(index)));
}
function updateIndices(items) {
  return items.sort(function (a, b) {
    return !a.element || !b.element ? 0 : isElementPreceding(a.element, b.element) ? -1 : 1;
  }).map(function (item, index) {
    return _objectSpread(_objectSpread({}, item), {}, {
      index: index
    });
  });
}
export { DescendantProvider, createDescendantContext, useDescendant, useDescendantKeyDown, useDescendants, useDescendantsInit };